import { useState, useEffect, useRef } from 'react';
import { TODO_VIEW_MODES } from '../constants';
import { collectNestedSprintIds } from '../utils/todoHelpers';

/**
 * Hook for loading nested sprints recursively when showing nested items
 * Works with both legacy viewMode and new showNestedItems prop
 */
export function useNestedSprintLoader({
  tasks,
  allSprints,
  loadFullSprint,
  viewMode,
  showNestedItems = true, // New prop - load nested sprints when true
}) {
  const [isLoadingNested, setIsLoadingNested] = useState(false);
  const loadedSprintIdsRef = useRef(new Set());
  const isLoadingRef = useRef(false);

  // Determine if we should load nested sprints
  // Either legacy MASTER mode or new showNestedItems toggle
  const shouldLoadNested = viewMode === TODO_VIEW_MODES.MASTER || showNestedItems;

  useEffect(() => {
    if (!shouldLoadNested) return;
    if (!loadFullSprint || tasks.length === 0) return;
    if (isLoadingRef.current) return;

    const loadNestedSprints = async () => {
      const neededIds = collectNestedSprintIds(tasks, allSprints);
      
      const unloadedIds = [...neededIds].filter(id => {
        const sprint = allSprints.find(s => s.id === id);
        return !sprint?.tasks && !loadedSprintIdsRef.current.has(id);
      });

      if (unloadedIds.length === 0) return;

      isLoadingRef.current = true;
      setIsLoadingNested(true);
      
      const loadPromises = unloadedIds.map(id => loadFullSprint(id));
      await Promise.all(loadPromises);
      
      unloadedIds.forEach(id => loadedSprintIdsRef.current.add(id));

      setIsLoadingNested(false);
      isLoadingRef.current = false;
    };

    loadNestedSprints();
  }, [tasks, allSprints, loadFullSprint, shouldLoadNested]);

  return { isLoadingNested };
}
